/**
 * Telephony / Ameyo calling APIs
 * Base: /prod/api/v1/telephony/calls
 * Admin: /prod/api/v1/admin/tenant-config/calling-config
 * Aligned with ameyo-integration-frontend-guide (1).md §8
 */

import { apiClient } from "./auth";
import type {
  AgentLoginPayload,
  AgentLoginResponse,
  AgentLogoutResponse,
  AgentStatusResponse,
  CallHistoryParams,
  CallHistoryResponse,
  CallMetricsParams,
  CallMetricsResponse,
  DeleteAgentProfileResponse,
  DeleteCallingConfigResponse,
  GetCallByIdResponse,
  GetCallingConfigResponse,
  InitiateCallPayload,
  InitiateCallResponse,
  ListAgentProfilesResponse,
  LogCallPayload,
  LogCallResponse,
  PendingFollowupsParams,
  PendingFollowupsResponse,
  SaveAgentProfilePayload,
  SaveAgentProfileResponse,
  ToolbarConfigResponse,
  UpsertCallingConfigPayload,
  UpsertCallingConfigResponse,
} from "../Interfaces/TelephonyInterface";

const BASE_PATH = "/telephony/calls";
const CALLING_CONFIG_PATH = "/admin/tenant-config/calling-config";

/**
 * GET /telephony/calls/toolbar-config
 * Returns Ameyo iframe URL and agent profile when calling is enabled.
 */
export const getToolbarConfigApi = async (): Promise<ToolbarConfigResponse> => {
  const response = await apiClient.get<ToolbarConfigResponse>(
    `${BASE_PATH}/toolbar-config`
  );
  return response.data;
};

/**
 * GET /telephony/calls/agent/status
 */
export const getAgentStatusApi = async (): Promise<AgentStatusResponse> => {
  const response = await apiClient.get<AgentStatusResponse>(
    `${BASE_PATH}/agent/status`
  );
  return response.data;
};

/**
 * POST /telephony/calls/agent/login — optional; iframe SSO is preferred.
 */
export const agentLoginApi = async (
  payload: AgentLoginPayload
): Promise<AgentLoginResponse> => {
  const response = await apiClient.post<AgentLoginPayload, AgentLoginResponse>(
    `${BASE_PATH}/agent/login`,
    payload
  );
  return response.data;
};

/**
 * POST /telephony/calls/agent/logout — optional.
 */
export const agentLogoutApi = async (): Promise<AgentLogoutResponse> => {
  const response = await apiClient.post<
    Record<string, never>,
    AgentLogoutResponse
  >(`${BASE_PATH}/agent/logout`, {});
  return response.data;
};

/**
 * POST /telephony/calls/initiate
 * Click-to-call via Ameyo (server-side).
 */
export const initiateCallApi = async (
  payload: InitiateCallPayload
): Promise<InitiateCallResponse> => {
  const response = await apiClient.post<
    InitiateCallPayload,
    InitiateCallResponse
  >(`${BASE_PATH}/initiate`, {
    mode: "CLICK_TO_CALL",
    ...payload,
  });
  return response.data;
};

/**
 * POST /telephony/calls/log
 * Post-call form submission; when provider is ameyo, backend pushes disposition to Ameyo.
 */
export const logCallApi = async (
  payload: LogCallPayload
): Promise<LogCallResponse> => {
  const response = await apiClient.post<LogCallPayload, LogCallResponse>(
    `${BASE_PATH}/log`,
    payload
  );
  return response.data;
};

/**
 * GET /telephony/calls/history
 */
export const getCallHistoryApi = async (
  params: CallHistoryParams = {}
): Promise<CallHistoryResponse> => {
  const search = new URLSearchParams();
  if (params.patientId) search.set("patientId", params.patientId);
  if (params.contactId) search.set("contactId", params.contactId);
  if (params.opportunityId) search.set("opportunityId", params.opportunityId);
  if (params.agentUserId) search.set("agentUserId", params.agentUserId);
  if (params.phoneNumber) search.set("phoneNumber", params.phoneNumber);
  if (params.limit != null) search.set("limit", String(params.limit));
  if (params.page != null) search.set("page", String(params.page));
  const qs = search.toString();
  const response = await apiClient.get<CallHistoryResponse>(
    `${BASE_PATH}/history${qs ? `?${qs}` : ""}`
  );
  return response.data;
};

/**
 * GET /telephony/calls/metrics
 */
export const getCallMetricsApi = async (
  params: CallMetricsParams
): Promise<CallMetricsResponse> => {
  const search = new URLSearchParams();
  search.set("startDate", params.startDate);
  search.set("endDate", params.endDate);
  if (params.facilityId) search.set("facilityId", params.facilityId);
  const response = await apiClient.get<CallMetricsResponse>(
    `${BASE_PATH}/metrics?${search.toString()}`
  );
  return response.data;
};

/**
 * GET /telephony/calls/pending-followups
 */
export const getPendingFollowupsApi = async (
  params: PendingFollowupsParams = {}
): Promise<PendingFollowupsResponse> => {
  const search = new URLSearchParams();
  if (params.limit != null) search.set("limit", String(params.limit));
  const qs = search.toString();
  const response = await apiClient.get<PendingFollowupsResponse>(
    `${BASE_PATH}/pending-followups${qs ? `?${qs}` : ""}`
  );
  return response.data;
};

/**
 * GET /telephony/calls/:id
 */
export const getCallByIdApi = async (
  callId: string
): Promise<GetCallByIdResponse> => {
  const response = await apiClient.get<GetCallByIdResponse>(
    `${BASE_PATH}/${encodeURIComponent(callId)}`
  );
  return response.data;
};

/**
 * GET /telephony/calls/agent/profiles — admin (CONFIGURE)
 */
export const listAgentProfilesApi =
  async (): Promise<ListAgentProfilesResponse> => {
    const response = await apiClient.get<ListAgentProfilesResponse>(
      `${BASE_PATH}/agent/profiles`
    );
    return response.data;
  };

/**
 * PUT /telephony/calls/agent/profiles/:userId — map CarePro user → Ameyo agent
 */
export const saveAgentProfileApi = async (
  userId: string,
  payload: SaveAgentProfilePayload
): Promise<SaveAgentProfileResponse> => {
  const response = await apiClient.put<
    SaveAgentProfilePayload,
    SaveAgentProfileResponse
  >(`${BASE_PATH}/agent/profiles/${encodeURIComponent(userId)}`, payload);
  return response.data;
};

/**
 * DELETE /telephony/calls/agent/profiles/:userId
 */
export const deleteAgentProfileApi = async (
  userId: string
): Promise<DeleteAgentProfileResponse> => {
  const response = await apiClient.delete<DeleteAgentProfileResponse>(
    `${BASE_PATH}/agent/profiles/${encodeURIComponent(userId)}`
  );
  return response.data;
};

/**
 * GET /admin/tenant-config/calling-config
 */
export const getCallingConfigApi =
  async (): Promise<GetCallingConfigResponse> => {
    const response = await apiClient.get<GetCallingConfigResponse>(
      CALLING_CONFIG_PATH
    );
    return response.data;
  };

/**
 * POST /admin/tenant-config/calling-config — create/update tenant Ameyo credentials
 */
export const upsertCallingConfigApi = async (
  payload: UpsertCallingConfigPayload
): Promise<UpsertCallingConfigResponse> => {
  const response = await apiClient.post<
    UpsertCallingConfigPayload,
    UpsertCallingConfigResponse
  >(CALLING_CONFIG_PATH, payload);
  return response.data;
};

/**
 * DELETE /admin/tenant-config/calling-config — disable calling for tenant
 */
export const deleteCallingConfigApi =
  async (): Promise<DeleteCallingConfigResponse> => {
    const response = await apiClient.delete<DeleteCallingConfigResponse>(
      CALLING_CONFIG_PATH
    );
    return response.data;
  };
